// @givmo/board — what the board left unfinished, and which minutes are still outstanding.
//
// ══ TWO SIBLING READS, NOT ONE MERGED LIST ═════════════════════════════════════════════════════
//
// Carry-forward items (agenda items the organization marked `unresolved`) and carry-forward motions
// (motions still `open` from past, non-cancelled meetings) arrive from two separate endpoints, and
// this panel keeps them in two separate lists. That diverges from what RC2's doc originally
// anticipated, and the reason is scoping: **the motion read is confidentiality-scoped and the agenda
// read is not.** A motion made inside an executive session must not appear for a non-participant, so
// merging the two would put the segment-scoping rule in a second service — exactly the shape the
// record core's one-rule discipline exists to prevent.
//
// ⚠ **BOTH SELECT ON THE ORGANIZATION'S OWN MARK AND NOTHING ELSE.** The module never infers that an
// un-dispositioned agenda item or an unvoted motion is unfinished business (§3.6 keeps elections and
// defaults with the organization). An item is here because somebody wrote down that it was unresolved.
//
// The clock list beside them is the Form 990 line-8 read over meetings AND executed written consents
// — one list because the instructions state one rule over both. A consent's record is filed on its
// completing signature, so its documentation instant IS its execution instant and it ordinarily reads
// as `recorded` the moment it lands; that is worth showing rather than hiding, because it is the
// organization's own evidence that it was.

import { useQuery } from '@tanstack/react-query';
import { Link } from '../../board-routing';

import { extractList } from '@givmo/api-client/list';
import type {
  BoardAgendaItemOut,
  BoardContemporaneityItemOut,
  BoardMotionOut,
} from '@givmo/api-client';
import { Badge, Card, CardBody } from '@givmo/design-system';
import { displayDate } from '../../lib/display';
import { clockStateLabel, clockTone, extractClockEntries } from '../minutes/minutes-decode';
import { useBoardOperations } from '../../board-runtime';
import { form990Contemporaneous } from './form990-date-words';
import { meetingCommitteeName } from './meetings-decode';

export interface CarryForwardPanelProps {
  orgRef: string;
}

export function CarryForwardPanel({ orgRef }: CarryForwardPanelProps) {
  const operations = useBoardOperations();
  const itemsQuery = useQuery({
    ...operations.officerListBoardCarryForwardItemsOptions({ path: { org_ref: orgRef } }),
    select: (body: unknown) => extractList<BoardAgendaItemOut>(body, 'data'),
  });

  const motionsQuery = useQuery({
    ...operations.officerListBoardCarryForwardMotionsOptions({ path: { org_ref: orgRef } }),
    select: (body: unknown) => extractList<BoardMotionOut>(body, 'data'),
  });

  const items = itemsQuery.data ?? [];
  const motions = motionsQuery.data ?? [];

  // Nothing carried forward is nothing to show. An empty "unfinished business" card on every screen
  // would train an officer to ignore it.
  if (items.length === 0 && motions.length === 0) return null;

  return (
    <Card testId="carry-forward-panel">
      <CardBody className="flex flex-col gap-12">
        <div className="flex flex-col gap-4">
          <h2 className="font-sans text-s16 font-w700 tracking-givmo text-text-strong">
            Carried forward
          </h2>
          <p className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary">
            What you marked unresolved at an earlier meeting, and motions still open from one.
          </p>
        </div>

        {items.length > 0 ? (
          <div className="flex flex-col gap-6" data-testid="carry-forward-items">
            <span className="font-sans text-s12 font-w600 tracking-givmo text-text-secondary">
              Agenda items
            </span>
            <ul className="flex flex-col gap-6">
              {items.map((item) => (
                <li
                  key={item.id}
                  className="flex flex-wrap items-center gap-8"
                  data-testid={`carry-forward-item-${item.id}`}
                >
                  <Link
                    to="/meetings/$meetingId"
                    params={{ meetingId: item.meeting_id }}
                    className="font-sans text-s13 font-w600 tracking-givmo text-brand-600 outline-none transition-colors hover:text-brand-700 focus-visible:underline"
                  >
                    {item.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {motions.length > 0 ? (
          <div className="flex flex-col gap-6" data-testid="carry-forward-motions">
            <span className="font-sans text-s12 font-w600 tracking-givmo text-text-secondary">
              Motions still open
            </span>
            <ul className="flex flex-col gap-6">
              {motions.map((motion) => (
                <li
                  key={motion.id}
                  className="flex flex-col gap-2"
                  data-testid={`carry-forward-motion-${motion.id}`}
                >
                  <Link
                    to="/meetings/$meetingId/record"
                    params={{ meetingId: motion.meeting_id }}
                    className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-brand-600 outline-none transition-colors hover:text-brand-700 focus-visible:underline"
                  >
                    {motion.motion_text}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </CardBody>
    </Card>
  );
}

export function ContemporaneityListPanel({ orgRef }: CarryForwardPanelProps) {
  const operations = useBoardOperations();
  const clockQuery = useQuery({
    ...operations.officerListBoardContemporaneityOptions({ path: { org_ref: orgRef } }),
    // The list read is a blind cast; `extractClockEntries` is what makes it a tolerant one.
    select: (body: unknown) =>
      extractClockEntries(extractList<BoardContemporaneityItemOut>(body, 'data')),
  });

  const entries = clockQuery.data ?? [];
  if (entries.length === 0) return null;
  // CBG-YR6-MEET: a committee's entry counts against the committee's own next meeting, so while one
  // is listed the lead names both bodies (the derivation §4.4); without one it is the board's.
  const anyCommittee = entries.some((entry) => meetingCommitteeName(entry) !== null);

  return (
    <Card testId="contemporaneity-list-panel">
      <CardBody className="flex flex-col gap-12">
        <div className="flex flex-col gap-4">
          <h2 className="font-sans text-s16 font-w700 tracking-givmo text-text-strong">
            Writing things up
          </h2>
          <p
            className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary"
            data-testid="contemporaneity-lead"
          >
            {/* Names the rule as the instructions' rule and stops. It does not say what happens if a
                date passes, and it does not say the organization must do anything by it. The rule's
                sentence is the to-do list's too (`form990-date-words.ts`). */}
            {`${form990Contemporaneous(anyCommittee)} These are your meetings and executed written consents against those dates.`}
          </p>
        </div>

        <ul className="flex flex-col gap-8" data-testid="contemporaneity-entries">
          {entries.map((entry) => (
            <li
              key={String(entry.reference_id)}
              className="flex flex-wrap items-center justify-between gap-8 rounded-lg border border-border-subtle p-12"
              data-testid={`contemporaneity-entry-${String(entry.reference_id)}`}
            >
              <div className="flex flex-col gap-2">
                <span className="font-sans text-s13 font-w600 tracking-givmo text-text-strong">
                  {/* CBG-YR6-MEET: a committee's meeting or written action leads with the committee,
                      counted against its own next meeting (the backend's sentence says so). */}
                  {meetingCommitteeName(entry) !== null ? (
                    <span className="text-text-secondary">{meetingCommitteeName(entry)} · </span>
                  ) : null}
                  {entry.title ?? '—'}
                </span>
                <span className="font-sans text-s12 font-w400 leading-h15 tracking-givmo text-text-tertiary">
                  {/* The backend's own sentence for this entry, verbatim — never a second phrasing. */}
                  {entry.clock.statement}
                </span>
                {entry.clock?.deadline_on ? (
                  <span className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary">
                    The later of the two: {displayDate(entry.clock.deadline_on)}
                  </span>
                ) : null}
              </div>
              <Badge tone={clockTone(entry.clock?.state)}>
                {clockStateLabel(entry.clock?.state)}
              </Badge>
            </li>
          ))}
        </ul>
      </CardBody>
    </Card>
  );
}
